import type { SelectedSlot } from "../bookingFlowMachine"
import { ScreeningProgram, useAvailability, type AvailabilitySlot } from "../hooks"
import {
  formatSlotTime,
  formatTimeZoneLabel,
  getAvailabilityWindow,
  getDisplayTimeZone,
  getLocalToday,
  getSlotLocalDate
} from "../timezone"
import {
  Alert,
  BackIcon,
  Box,
  Button,
  ForwardIcon,
  IconButton,
  Progress,
  Theme,
  Tile,
  Typography,
  dayjs,
  useMediaQuery
} from "@carrotfertility/carotene-core"
import { FormButton, useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { useNow } from "@internal/ui"
import React from "react"
import { useIntl } from "react-intl"
import { Link } from "react-router-dom"
import { useBookingTracking } from "../tracking"

// 6 days per page (matching the design), which also divides the 30-day horizon evenly.
const DAYS_PER_PAGE = 6

const MOBILE_DAYS_PER_PAGE = 3
// The UTC request includes a buffer; the member's local booking horizon remains 30 days.
const HORIZON_DAYS = 30

// The subset of the preferences form this section reads/writes. `selectedSlot` matches the
// machine's SelectedSlot so it can be handed straight to CONFIRM_BOOKING.
type AvailabilityFormValues = {
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

type AppointmentAvailabilityProps = {
  program: ScreeningProgram
  // The submitted visit type + location of care that key the availability query. This
  // component only renders once the member has submitted those (see SelectPreferencesPage),
  // so both are always present. `offeringId` is the chosen offering's scheduling-api id.
  offeringId: string
  stateCode: string
  // State of the booking call the parent fires on submit — drives the CTA's pending/disabled
  // state and the inline error on failure.
  booking: { isPending: boolean; isError: boolean }
  onBack: () => void
  timeZone?: string
}

// The date strip + "Available times" grid, revealed after the member submits their visit
// preferences (MEM-716). Availability comes back as UTC slots; everything here is rendered in
// the specified display timezone (defaulting to the browser's local timezone).
export function AppointmentAvailability({
  program,
  offeringId,
  stateCode,
  booking,
  onBack,
  timeZone
}: AppointmentAvailabilityProps): JSX.Element {
  const intl = useIntl()
  const tracking = useBookingTracking()
  const displayTimeZone = getDisplayTimeZone(timeZone)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const { setValue } = useFormContext<AvailabilityFormValues>()
  const selectedDate = useWatch<AvailabilityFormValues, "selectedDate">({ name: "selectedDate" })
  const selectedSlot = useWatch<AvailabilityFormValues, "selectedSlot">({ name: "selectedSlot" })

  // Which page of dates the strip is showing; 0 is the page starting today.
  const [weekOffset, setWeekOffset] = React.useState(0)

  // Represent local calendar dates in UTC only for date arithmetic, so DST cannot change pagination.
  const todayDate = getLocalToday(displayTimeZone).format("YYYY-MM-DD")
  const today = dayjs.utc(todayDate)
  const horizonEndDate = today.add(HORIZON_DAYS, "day").format("YYYY-MM-DD") // exclusive

  // Re-render at local midnight, which can be 23 or 25 hours away across DST.
  const tomorrowDate = today.add(1, "day").format("YYYY-MM-DD")
  useNow(dayjs.tz(tomorrowDate, displayTimeZone).toDate(), "at-the-crossing-only")

  React.useEffect(() => {
    setWeekOffset(0)
    setValue("selectedDate", null)
    setValue("selectedSlot", null)
  }, [todayDate, displayTimeZone, setValue])

  const availabilityWindow = React.useMemo(
    () => getAvailabilityWindow(todayDate, horizonEndDate, displayTimeZone),
    [todayDate, horizonEndDate, displayTimeZone]
  )

  // Query the whole bookable horizon [today, today + 30 days) at once. This tells us whether
  // ANY dates are available (for the empty state) and lets us page through them client-side.
  const availabilityQuery = useAvailability({
    program,
    offeringId,
    stateCode,
    language: intl.locale,
    from: availabilityWindow.from,
    to: availabilityWindow.to
  })

  const daysPerPage = isMobile ? MOBILE_DAYS_PER_PAGE : DAYS_PER_PAGE

  // The visible page of dates (display only — the query already spans the full horizon). Drop
  // any day at/after the horizon so the strip never shows a date more than 30 days out.
  const pageStart = today.add(weekOffset * daysPerPage, "day")
  const days = Array.from({ length: daysPerPage }, (_, index) => pageStart.add(index, "day")).filter(
    (day) => day.format("YYYY-MM-DD") < horizonEndDate
  )
  const canGoNext = pageStart.add(daysPerPage, "day").format("YYYY-MM-DD") < horizonEndDate

  // UTC padding may contain slots outside the local horizon; exclude them from every availability decision.
  const slotsByDate = React.useMemo(() => {
    const grouped = new Map<string, AvailabilitySlot[]>()
    if (availabilityQuery.data?.outcome !== "slots_available") return grouped
    for (const slot of availabilityQuery.data.slots) {
      const date = getSlotLocalDate(slot.startTime, displayTimeZone)
      if (date < todayDate || date >= horizonEndDate) continue
      const daySlots = grouped.get(date)
      if (daySlots) daySlots.push(slot)
      else grouped.set(date, [slot])
    }
    return grouped
  }, [availabilityQuery.data, displayTimeZone, todayDate, horizonEndDate])
  const hasSlots = slotsByDate.size > 0
  const outcome =
    availabilityQuery.data?.outcome === "slots_available" && availabilityQuery.data.slots.length > 0 && !hasSlots
      ? "no_availability"
      : availabilityQuery.data?.outcome
  const searchKey = `${offeringId}:${stateCode}:${intl.locale}:${todayDate}`
  React.useEffect(() => {
    if (availabilityQuery.isLoading || !outcome) return
    tracking.onEvent({
      type: "availability",
      searchKey,
      outcome: !availabilityQuery.isError && !hasSlots ? outcome : null
    })
  }, [tracking, searchKey, outcome, hasSlots, availabilityQuery.isLoading, availabilityQuery.isError])

  // Slots that fall on the selected day in the display timezone.
  const slotsForSelectedDate = slotsByDate.get(selectedDate ?? "") ?? []

  const chooseDate = (date: string) => {
    setValue("selectedDate", date)
    // A new day invalidates any previously chosen time.
    setValue("selectedSlot", null)
  }

  const chooseSlot = (slot: AvailabilitySlot) => {
    setValue("selectedSlot", {
      slotToken: slot.slotToken,
      startTime: slot.startTime,
      offeringId: slot.offeringId,
      availableHostIds: slot.availableHostIds
    })
  }

  const goToPage = (offset: number) => {
    setWeekOffset(offset)
    // The visible dates shift out from under the selection; clear it.
    setValue("selectedDate", null)
    setValue("selectedSlot", null)
  }

  if (availabilityQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }
  if (availabilityQuery.isError) {
    return <Alert severity="error">Something went wrong loading appointment times. Please try again.</Alert>
  }
  // Nothing to book — show the empty state instead of the date strip, with a disabled "Continue"
  // CTA (matching the design). One message covers every no-slots outcome for now; telling
  // no_licensed_provider apart from no_availability for the member is designed but not built.
  if (!hasSlots) {
    return (
      <Tile
        color="default"
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          backgroundColor: theme.palette.background.paper,
          gap: theme.spacing(theme.tokens.spacing.lg)
        })}
      >
        <div>
          <Typography variant="h6" sx={(theme) => ({ marginBlockEnd: theme.spacing(theme.tokens.spacing.xs) })}>
            No appointments available right now
          </Typography>
          <Typography variant="body1">
            There are no open times for an initial consult in the next 30 days. Your Carrot Companion can help you find
            care sooner.
          </Typography>
        </div>
        <Button component={Link} to={"/messages"} variant="contained" color="secondary">
          Send a message
        </Button>
      </Tile>
    )
  }

  return (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xl) })}>
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <Typography variant="h5" component="h2">
          Select a date
        </Typography>
        <Box sx={(theme) => ({ display: "flex", alignItems: "stretch", gap: theme.spacing(theme.tokens.spacing.xs) })}>
          {weekOffset > 0 && (
            <StripChevron label="Previous dates" icon={<BackIcon />} onClick={() => goToPage(weekOffset - 1)} />
          )}
          {days.map((day) => {
            const date = day.format("YYYY-MM-DD")
            return (
              <DateCard
                key={date}
                day={day}
                selected={selectedDate === date}
                onSelect={() => chooseDate(date)}
                disabled={!slotsByDate.has(date)}
              />
            )
          })}
          {canGoNext && (
            <StripChevron label="Next dates" icon={<ForwardIcon />} onClick={() => goToPage(weekOffset + 1)} />
          )}
        </Box>
      </Box>

      {selectedDate && (
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Typography variant="h5" component="h2">
            Available times
          </Typography>
          {slotsForSelectedDate.length === 0 ? (
            <Typography color="text.secondary">
              No appointment times are available for this date. Try another date or location.
            </Typography>
          ) : (
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                gap: theme.spacing(theme.tokens.spacing.sm),
                maxInlineSize: 560
              })}
            >
              {slotsForSelectedDate.map((slot) => (
                <TimeSlotButton
                  key={slot.slotToken}
                  label={formatSlotTime(slot.startTime, displayTimeZone)}
                  selected={selectedSlot?.slotToken === slot.slotToken}
                  onSelect={() => chooseSlot(slot)}
                />
              ))}
            </Box>
          )}
          {slotsForSelectedDate.length > 0 && (
            <Typography variant="body1">
              Times are in {formatTimeZoneLabel(slotsForSelectedDate[0].startTime, displayTimeZone)}
            </Typography>
          )}
        </Box>
      )}

      {booking.isError && (
        <Alert severity="error">Something went wrong booking your appointment. Please try again.</Alert>
      )}

      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(theme.tokens.spacing.sm)
        })}
      >
        <FormButton color="secondary" variant="text" startIcon={<BackIcon />} onClick={onBack}>
          Back
        </FormButton>
        <FormButton
          type="submit"
          color="secondary"
          endIcon={<ForwardIcon />}
          disabled={!selectedSlot || booking.isPending}
        >
          Confirm booking
        </FormButton>
      </Box>
    </Box>
  )
}

type DateCardProps = { day: dayjs.Dayjs; selected: boolean; onSelect: () => void; disabled?: boolean }

// One day in the date strip: a tall card of weekday / day-of-month / month, as a selectable
// button. Cards share the row width equally (flex: 1), matching the design.
function DateCard({ day, selected, onSelect, disabled = false }: DateCardProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={day.format("dddd, MMMM D")}
      sx={(theme) => ({
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        justifyContent: "space-between",
        gap: theme.spacing(theme.tokens.spacing.xxxs),
        minInlineSize: 56,
        minBlockSize: 104,
        paddingBlock: theme.spacing(theme.tokens.spacing.md),
        paddingInline: theme.spacing(theme.tokens.spacing.xs),
        backgroundColor: disabled ? "unset" : theme.palette.background.paper,
        border: disabled
          ? "none"
          : `${theme.tokens.borderWidth.md} solid ${
              // secondary is a workaround because border.light was too dark.
              selected ? theme.palette.secondary.dark : "transparent"
            }`,
        borderRadius: theme.tokens.borderRadius.md
      })}
    >
      <Typography variant="body2" component="span">
        {day.format("ddd")}
      </Typography>
      <Typography
        variant="h3"
        sx={(theme) => ({
          fontWeight: theme.typography.fontWeightBold,
          color: disabled ? theme.palette.text.disabled : theme.palette.text.primary
        })}
        component="span"
      >
        {day.format("D")}
      </Typography>
      <Typography variant="body2" component="span">
        {day.format("MMM")}
      </Typography>
    </Box>
  )
}

type StripChevronProps = { label: string; icon: JSX.Element; onClick: () => void }

// The date-strip pager control, styled as a card-height bordered box to match the design.
function StripChevron({ label, icon, onClick }: StripChevronProps): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        inlineSize: 48,
        minBlockSize: 104
      }}
    >
      <IconButton
        sx={(theme) => ({
          border: `${theme.tokens.borderWidth.md} solid ${theme.palette.secondary.dark}`,
          borderRadius: theme.tokens.borderRadius.round
        })}
        onClick={onClick}
        aria-label={label}
      >
        {icon}
      </IconButton>
    </Box>
  )
}

type TimeSlotButtonProps = { label: string; selected: boolean; onSelect: () => void }

// One appointment time, as a selectable button.
function TimeSlotButton({ label, selected, onSelect }: TimeSlotButtonProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={(theme) => ({
        paddingBlock: theme.spacing(theme.tokens.spacing.sm),
        paddingInline: theme.spacing(theme.tokens.spacing.md),
        cursor: "pointer",
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.md} solid ${
          // secondary is a workaround because border.light was too dark.
          selected ? theme.palette.secondary.dark : "transparent"
        }`,
        borderRadius: theme.tokens.borderRadius.md,
        font: "inherit",
        color: theme.palette.text.primary
      })}
    >
      {label}
    </Box>
  )
}
